<template>
  <panel-layout
    id="purchase-order-panel"
    :loading="loading"
    :show-footer="false"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template v-if="purchaseOrder" #actions-left>
      <o-tooltip v-if="isStatusOpen" :text="$t('pages.purchaseOrder.edit.markAsSent')">
        <o-button icon="send" flat @click="handleSend">
          {{ $t('purchaseOrder.markAsSent') }}
        </o-button>
      </o-tooltip>

      <o-tooltip :text="$t('pages.purchaseOrder.edit.pdf')">
        <o-button icon="print" flat @click="handlePrint">
          {{ $t('pages.purchaseOrder.edit.print') }}
        </o-button>
      </o-tooltip>

      <o-tooltip v-if="isStatusSent" :text="$t('pages.purchaseOrder.edit.markAsReceiving')">
        <o-button icon="receive" flat @click="handleReceive">
          {{ $t('pages.purchaseOrder.edit.receive') }}
        </o-button>
      </o-tooltip>

      <o-tooltip v-if="isStatusReceiving" :text="$t('pages.purchaseOrder.edit.markAsComplete')">
        <o-button icon="success" flat @click="handleComplete">
          {{ $t('general.complete') }}
        </o-button>
      </o-tooltip>
    </template>


    <template v-if="purchaseOrder" #actions-right>
      <o-button v-if="isStatusOpen" icon="edit" flat @click="handleDetails" />
      <o-button icon="print" flat @click="handlePrint" />
    </template>

    <template #heading>
      <div v-if="purchaseOrder" class="flex justify-between items-center">
        <div>
          <h3 class="text-xl tracking-tight text-gray-800 dark:text-gray-100 font-bold leading-none mr-3 mb-2">
            {{ $t('pages.purchaseOrder.edit.label', { name: purchaseOrder.supplier.name, number: purchaseOrder.purchaseOrderNumber }) }}
          </h3>

          <o-chip v-if="isStatusOpen" variant="warning">
            {{ $t('general.open') }}
          </o-chip>

          <o-chip v-if="isStatusSent" variant="warning">
            {{ $t('general.sent') }}
          </o-chip>

          <o-chip v-if="isStatusReceiving" variant="info">
            {{ $t('order.receiving') }}
          </o-chip>

          <o-chip v-if="isStatusComplete" variant="success">
            {{ $t('general.complete') }}
          </o-chip>
        </div>
      </div>
    </template>

    <div v-if="purchaseOrder" class="h-full">
      <o-scroll>
        <div class="w-full px-6 py-2">
          <div class="border-b mt-4 pb-2 mb-2">
            <label class="block text-sm text-gray-700 dark:text-gray-200 font-medium" tabindex="0">
              <span>{{ $t('general.details') }}</span>
            </label>
          </div>

          <o-description-details :term="$t('general.employee')">
            <template v-if="purchaseOrder.employee">
              <span class="flex items-center leading-tight">
                <o-avatar :items="[ purchaseOrder.employee ]" class="hidden md:flex pr-2" />
                <span>{{ purchaseOrder.employee.displayName }}</span>
              </span>
            </template>
            <template v-else>
              -
            </template>
          </o-description-details>

          <o-description-details v-if="purchaseOrder.date" :term="$t('general.date')">
            {{ $date.format(purchaseOrder.date * 1000, $date.presets.full) }}
          </o-description-details>

          <o-description-details :term="$t('general.notes')">
            {{ purchaseOrder.notes || '-' }}
          </o-description-details>

          <o-description-details :term="$t('purchaseOrder.fromAddress')">
            {{ purchaseOrder.addressFrom || '-' }}
          </o-description-details>

          <o-description-details :term="$t('purchaseOrder.toAddress')">
            {{ purchaseOrder.addressTo || '-' }}
          </o-description-details>

          <o-description-details :term="$t('purchaseOrder.shippingAddress')">
            {{ purchaseOrder.addressShip || '-' }}
          </o-description-details>

          <div class="flex items-center justify-between pb-2 mt-8 border-b">
            <div>
              <label class="block text-sm text-gray-700 dark:text-gray-200 font-medium" tabindex="0">
                <span>{{ $t('pages.purchaseOrder.edit.items') }}</span>
              </label>
              <p class="block text-gray-600 dark:text-gray-300 text-sm">
                {{ $t('pages.purchaseOrder.edit.products') }}
              </p>
            </div>

            <o-button v-if="isStatusOpen" icon="add" variant="info" @click="handleAdd">
              {{ $t('pages.purchaseOrder.edit.addItems') }}
            </o-button>

            <o-button v-if="isStatusReceiving" variant="info" @click="handleReceiveAll">
              {{ $t('pages.purchaseOrder.edit.receiveAll') }}
            </o-button>
          </div>

          <o-table
            v-if="purchaseOrder.data.items"
            class="py-5 w-full"
            container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
            v-model:data="purchaseOrder.data.items"
            :headers="[
              {text: $t('general.brand')},
              {text: $t('general.product')},
              {text: $t('general.category')},
              {text: $t('general.code')},
              {text: $t('general.price')},
              {text: $t('general.quantity')},
              {text: $t('general.received')},
              {text: $t('general.actions')}
            ]"
            :tbody-class="{
              tfoot: '',
              tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
              td: 'py-3 px-4 text-gray-800 dark:text-gray-100 text-sm',
              stripe: 'bg-gray-50/50 dark:bg-gray-900'
            }"
            :thead-class="{
              thead: 'border-b',
              tr: 'border-blue-700',
              th: 'font-normal p-4 pb-3 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-200 text-sm sticky top-0',
              content: 'flex item-center'
            }"
            :limit="20"
            striped
          >
            <template #row="props">
              <td :class="props.tdClass">
                {{ props.row.brand ? props.row.brand : '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.title }}
                <p v-if="props.row.notes" class="text-xs text-gray-600 dark:text-gray-300">
                  {{ $t('pages.purchaseOrder.edit.notes', { notes: props.row.notes }) }}
                </p>
              </td>
              <td :class="props.tdClass">
                {{ props.row.productCategory?.name || '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.code || '-' }}
              </td>
              <td :class="props.tdClass">
                {{ $currency.format(props.row.price) }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.orderQuantity }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.receivedQuantity }}
              </td>
              <td :class="props.tdClass" class="w-1">
                <div class="flex items-center space-x-1">
                  <o-tooltip v-if="isStatusOpen" :text="$t('general.edit')">
                    <o-button icon="edit" size="sm" flat @click="handleEdit(props.row)" />
                  </o-tooltip>
                  <o-tooltip v-if="isStatusOpen" :text="$t('general.delete')">
                    <o-button icon="delete" size="sm" variant="danger" flat @click="handleRemove(props.row)" />
                  </o-tooltip>
                  <o-tooltip v-if="isStatusReceiving" :text="$t('pages.purchaseOrder.edit.setQuantity')">
                    <o-button size="sm" flat @click="handleReceiveItem(props.row)">
                      {{ $t('pages.purchaseOrder.edit.receive') }}
                    </o-button>
                  </o-tooltip>
                </div>
              </td>
            </template>
          </o-table>

          <div class="mt-8">
            <div class="border-b mb-2 pb-2">
              <label class="block text-sm text-gray-700 dark:text-gray-200 font-medium" tabindex="0">
                <span>{{ $t('pages.purchaseOrder.edit.totals') }}</span>
              </label>
            </div>

            <o-description-details :term="$t('general.subtotal')">
              {{ $currency.format(purchaseOrder.data.totals.subtotal) }}
            </o-description-details>

            <o-description-details v-for="tax in purchaseOrder.data.taxTotals" :key="tax.tax.idTax" :term="tax.tax.name">
              {{ $currency.format(tax.total) }}
            </o-description-details>

            <o-description-details :term="$t('general.total')">
              {{ $currency.format(purchaseOrder.data.totals.total) }}
            </o-description-details>
          </div>
        </div>
      </o-scroll>
    </div>

    <details-modal
      v-if="purchaseOrder"
      ref="detailsModal"
      v-model:purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <add-modal
      v-if="purchaseOrder"
      ref="addModal"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <edit-modal
      v-if="purchaseOrder"
      ref="editModal"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <remove-dialog
      v-if="purchaseOrder"
      ref="removeDialog"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <send-modal
      v-if="purchaseOrder"
      ref="sendDialog"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <receive-dialog
      v-if="purchaseOrder"
      ref="receiveDialog"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <receive-item-dialog
      v-if="purchaseOrder"
      ref="receiveItemDialog"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <receive-all-dialog
      v-if="purchaseOrder"
      ref="receiveAllDialog"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <print-dialog
      v-if="purchaseOrder"
      ref="printDialog"
    />

    <complete-modal
      v-if="purchaseOrder"
      ref="completeDialog"
      :purchase-order="purchaseOrder"
      @success="refreshPurchaseOrder"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import DetailsModal from '@/components/purchaseOrder/actions/Details.vue'
// @ts-ignore
import AddModal from '@/components/purchaseOrder/actions/Add.vue'
// @ts-ignore
import RemoveDialog from '@/components/purchaseOrder/actions/Remove.vue'
// @ts-ignore
import EditModal from '@/components/purchaseOrder/actions/Edit.vue'
// @ts-ignore
import SendModal from '@/components/purchaseOrder/actions/Send.vue'
// @ts-ignore
import ReceiveDialog from '@/components/purchaseOrder/actions/Receive.vue'
// @ts-ignore
import ReceiveItemDialog from '@/components/purchaseOrder/actions/ReceiveItem.vue'
// @ts-ignore
import ReceiveAllDialog from '@/components/purchaseOrder/actions/ReceiveAll.vue'
// @ts-ignore
import PrintDialog from '@/components/purchaseOrder/actions/Print.vue'
// @ts-ignore
import CompleteModal from '@/components/purchaseOrder/actions/Complete.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  DateField,
  TextField,
  TextAreaField,
} from '@/components/fields'

import {
  ODescriptionDetails,
  OTable,
  OAvatar,
} from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const purchaseOrder = ref<any>(null)
const loading = ref(true)

const confirmDialog = ref<any>()
const detailsModal = ref<any>()
const addModal = ref<any>()
const editModal = ref<any>()
const removeDialog = ref<any>()
const sendDialog = ref<any>()
const receiveDialog = ref<any>()
const receiveItemDialog = ref<any>()
const receiveAllDialog = ref<any>()
const printDialog = ref<any>()
const completeDialog = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(purchaseOrder)

const isStatusOpen = computed(() => purchaseOrder.value.isStatusOpen)
const isStatusSent = computed(() => purchaseOrder.value.isStatusSent)
const isStatusReceiving = computed(() => purchaseOrder.value.isStatusReceiving)
const isStatusComplete = computed(() => purchaseOrder.value.isStatusComplete)

const fetchPurchaseOrder = async (id?: string) => {
  if (!id) {
    loading.value = false
    return
  }

  loading.value = true

  try {
    purchaseOrder.value = (await $api.purchaseOrder.get(id)).data
    snapshot()
    $bus.emit.updated('PurchaseOrder', purchaseOrder.value)
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const refreshPurchaseOrder = async () => {
  await fetchPurchaseOrder(props.id)
}

const handleDetails = () => {
  detailsModal.value.open(purchaseOrder.value)
}

const handleAdd = () => {
  addModal.value.open()
}

const handleEdit = (productOrderItem: any) => {
  editModal.value.open(productOrderItem)
}

const handleRemove = (productOrderItem: any) => {
  removeDialog.value.open(productOrderItem)
}

const handlePrint = () => {
  printDialog.value.open(purchaseOrder.value)
}

const handleSend = () => {
  sendDialog.value.open(purchaseOrder.value)
}

const handleReceive = () => {
  receiveDialog.value.open(purchaseOrder.value)
}

const handleReceiveItem = (purchaseOrderItem: any) => {
  receiveItemDialog.value.open(purchaseOrder.value, purchaseOrderItem)
}

const handleReceiveAll = () => {
  receiveAllDialog.value.open(purchaseOrder.value)
}

const handleComplete = () => {
  completeDialog.value.open(purchaseOrder.value)
}

const save = async (close = false) => {
  loading.value = true

  try {
    const _purchaseOrder = (await $api.purchaseOrder.save(purchaseOrder.value)).data

    if (_purchaseOrder) {
      purchaseOrder.value = _purchaseOrder
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.purchaseOrder.edit.success'),
        text: $i18n.t('pages.purchaseOrder.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('PurchaseOrder', _purchaseOrder)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })

    loading.value = false
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

watch(() => props.id, (id) => fetchPurchaseOrder(id), { immediate: true })
</script>
